import {
  addDays,
  fromYmd,
  weekDays,
  ymd,
  type EntryInput,
  type MyTimesheetDTO,
} from "@contractorhub/shared";
import { defaultTypeForDay, type Row } from "./types";

/**
 * A week that straddles a month end is two timesheets per project (TIME-1), so
 * a project id no longer names a row on its own: the period does too.
 */
export function rowKey(row: Pick<Row, "projectId" | "periodStart">): string {
  return `${row.projectId}@${row.periodStart}`;
}

/** Whether the row's period covers day `day` of its week (0 = Monday). */
export function coversDay(
  row: Pick<Row, "weekStart" | "periodStart" | "periodEnd">,
  day: number
): boolean {
  const date = ymd(addDays(fromYmd(row.weekStart), day));
  return date >= row.periodStart && date <= row.periodEnd;
}

/**
 * The editor's shape for one period: seven entries, Monday first.
 *
 * The editor indexes every day by its distance from Monday — the day rows, the
 * caps, the weekend fold — and a half-week arriving with two or four entries
 * would shift every one of them. So the period's own entries keep their place,
 * and the days another row covers hold inert placeholders: no hours, no note,
 * the day's default type. They are never rendered against this row and never
 * sent (`periodEntries`), and every count over them adds zero.
 */
export function toWeekRow(sheet: MyTimesheetDTO): Row {
  const own = new Map(sheet.entries.map((entry) => [entry.date, entry]));
  const entries = weekDays(fromYmd(sheet.weekStart)).map((day, index): EntryInput => {
    const date = ymd(day);
    return own.get(date) ?? { date, type: defaultTypeForDay(index), hours: 0, comment: "" };
  });
  return { ...sheet, entries };
}

/** What the API stores for this row: one entry per day of its period (TIME-3). */
export function periodEntries(row: Row): EntryInput[] {
  return row.entries.filter(
    (entry) => entry.date >= row.periodStart && entry.date <= row.periodEnd
  );
}

/** Whether the row is half of a split week rather than the whole week. */
export function isHalfWeek(row: Pick<Row, "weekStart" | "periodStart" | "periodEnd">): boolean {
  return (
    row.periodStart !== row.weekStart ||
    row.periodEnd !== ymd(addDays(fromYmd(row.weekStart), 6))
  );
}
